import { useState, useMemo } from 'react'
import { channelColor, resolveChannel, DEFAULT_COLORS } from '../lib/channels'

// Path steps are the classifier's labels ('Meta', 'Direct', ...), so they take
// the app-wide channel colour from lib/channels. This file used to keep its own
// map, which had Google red, Organic green and Email violet — the same channel
// a different colour here than on every other report.
const LIGHT_BADGE_TEXT = '#111827'

// Dark text on light fills (Snapchat yellow), white on everything else.
function badgeTextColor(hex) {
  const n = parseInt(hex.slice(1), 16)
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255
  return (0.299 * r + 0.587 * g + 0.114 * b) > 186 ? LIGHT_BADGE_TEXT : '#FFFFFF'
}

// Get badge style for a source. A step nothing recognises reads as Other (gray)
// rather than taking a positional default colour that could look like a channel.
const getSourceStyle = (source) => {
  let bg = channelColor(source)
  if (!resolveChannel(source) && DEFAULT_COLORS.includes(bg)) bg = channelColor('other')
  return { backgroundColor: bg, color: badgeTextColor(bg) }
}

// Get CVR badge style based on value
const getCvrStyle = (cvr) => {
  if (cvr >= 15) return { backgroundColor: '#22C55E', color: '#FFFFFF' }  // Green
  if (cvr >= 10) return { backgroundColor: '#84CC16', color: '#FFFFFF' }  // Lime
  if (cvr >= 5) return { backgroundColor: '#EAB308', color: '#000000' }   // Yellow
  if (cvr >= 2) return { backgroundColor: '#F97316', color: '#FFFFFF' }   // Orange
  return { backgroundColor: '#EF4444', color: '#FFFFFF' }                  // Red
}

/**
 * Visual path representation showing journey steps as badges
 */
export function PathVisualization({ pathArray }) {
  if (!pathArray || pathArray.length === 0) {
    return <span className="text-muted-foreground">-</span>
  }

  return (
    <div className="flex items-center gap-1 overflow-x-auto max-w-[500px] pb-1 scrollbar-thin">
      {pathArray.map((step, idx) => (
        <span key={idx} className="flex items-center shrink-0">
          <span
            className="px-2 py-0.5 text-xs font-medium rounded whitespace-nowrap"
            style={getSourceStyle(step)}
          >
            {step}
          </span>
          {idx < pathArray.length - 1 && (
            <span className="mx-1 text-muted-foreground">→</span>
          )}
        </span>
      ))}
    </div>
  )
}

/**
 * Conversion paths table component
 *
 * @param {Object} props
 * @param {Array} props.paths - Array of path objects with path, journeyCount, converted, cvr
 * @param {Object} props.summary - Summary stats
 * @param {boolean} props.loading - Show loading state
 * @param {string} props.emptyMessage - Message when no data
 * @param {Function} props.onViewUsers - Optional callback(pathItem) to view the users behind a path
 */
export default function PathsTable({
  paths = [],
  summary = null,
  loading = false,
  emptyMessage = 'No paths found for the selected filters',
  onViewUsers = null
}) {
  const [sortField, setSortField] = useState('journeyCount')
  const [sortDirection, setSortDirection] = useState('desc')
  const [searchQuery, setSearchQuery] = useState('')

  // Filter and sort paths
  const filteredPaths = useMemo(() => {
    let result = [...paths]

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(
        p => p.path?.toLowerCase().includes(query) ||
             p.pathArray?.some(s => s.toLowerCase().includes(query))
      )
    }

    // Sort
    result.sort((a, b) => {
      const aVal = a[sortField] || 0
      const bVal = b[sortField] || 0
      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal
    })

    return result
  }, [paths, searchQuery, sortField, sortDirection])

  // Handle sort click
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  // Sort indicator
  const SortIndicator = ({ field }) => {
    if (sortField !== field) return null
    return (
      <span className="ml-1">
        {sortDirection === 'desc' ? '↓' : '↑'}
      </span>
    )
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-background rounded-lg border border-border p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-3"></div>
          <span className="text-muted-foreground">Loading conversion paths...</span>
        </div>
      </div>
    )
  }

  // Empty state
  if (!paths || paths.length === 0) {
    return (
      <div className="bg-background rounded-lg border border-border p-8">
        <div className="text-center text-muted-foreground">
          <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p>{emptyMessage}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Summary row */}
      {summary && (
        <div className="flex flex-wrap gap-4 p-4 bg-muted/50 rounded-lg">
          <div>
            <span className="text-sm text-muted-foreground">Total Journeys: </span>
            <span className="font-semibold text-foreground">
              {(summary.totalJourneys || summary.total_journeys || 0).toLocaleString()}
            </span>
          </div>
          <div>
            <span className="text-sm text-muted-foreground">Converted: </span>
            <span className="font-semibold text-foreground">
              {(summary.totalConverted || summary.converted || 0).toLocaleString()}
            </span>
          </div>
          <div>
            <span className="text-sm text-muted-foreground">Overall CVR: </span>
            <span
              className="px-2 py-0.5 text-sm font-medium rounded"
              style={getCvrStyle(summary.overallCvr || summary.cvr || 0)}
            >
              {(summary.overallCvr || summary.cvr || 0).toFixed(1)}%
            </span>
          </div>
          <div>
            <span className="text-sm text-muted-foreground">Unique Paths: </span>
            <span className="font-semibold text-foreground">
              {paths.length.toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search paths..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 pl-10 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
        />
        <svg className="absolute left-3 top-2.5 w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Journey Path
              </th>
              <th
                className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                onClick={() => handleSort('journeyCount')}
              >
                Journeys <SortIndicator field="journeyCount" />
              </th>
              <th
                className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                onClick={() => handleSort('converted')}
              >
                Converted <SortIndicator field="converted" />
              </th>
              <th
                className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                onClick={() => handleSort('notConverted')}
              >
                Not Converted <SortIndicator field="notConverted" />
              </th>
              <th
                className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                onClick={() => handleSort('cvr')}
              >
                CVR <SortIndicator field="cvr" />
              </th>
              {onViewUsers && (
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Users
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-background divide-y divide-border">
            {filteredPaths.map((pathItem, idx) => (
              <tr key={idx} className="hover:bg-muted/50">
                <td className="px-4 py-3">
                  <PathVisualization pathArray={pathItem.pathArray || pathItem.path?.split(' -> ')} />
                </td>
                <td className="px-4 py-3 text-right text-sm text-foreground font-medium">
                  {(pathItem.journeyCount || pathItem.journey_count || 0).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right text-sm font-medium" style={{ color: '#22C55E' }}>
                  {(pathItem.converted || 0).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right text-sm text-muted-foreground">
                  {(pathItem.notConverted || pathItem.not_converted || 0).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right">
                  <span
                    className="px-2 py-0.5 text-sm font-medium rounded"
                    style={getCvrStyle(pathItem.cvr || 0)}
                  >
                    {(pathItem.cvr || 0).toFixed(1)}%
                  </span>
                </td>
                {onViewUsers && (
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => onViewUsers(pathItem)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary border border-border rounded-md hover:bg-muted whitespace-nowrap"
                      title="Get user IDs for this path"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6-4a4 4 0 11-8 0" />
                      </svg>
                      User IDs
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Results count */}
      {searchQuery && (
        <div className="text-sm text-muted-foreground">
          Showing {filteredPaths.length} of {paths.length} paths
        </div>
      )}
    </div>
  )
}
